import { Head, router, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, applyFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, StackedBarChart, ChartColors } from '@/components/ReportCharts'

interface Row {
  id: number
  root_name: string
  downline_size: number
  producing_agents: number
  gross_amount: number
  agent_amount: number
  override_earned: number
  equity_credits: number
}

interface DrillRow {
  id: number
  agent_name: string
  policies: number
  gross_amount: number
  agent_amount: number
  equity_credits: number
}

interface Props extends PageProps {
  rows: Row[]
  drill_rows: DrillRow[]
  drill_root_id: number | null
  drill_root_name: string | null
  date_range: DateRange
  all_agents: { id: number; name: string }[]
  filters: { root_agent_id: number | null; drill_root_id: number | null }
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/downline_production'

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export default function DownlineProduction({ rows, drill_rows, drill_root_id, drill_root_name, date_range, all_agents, filters, sort }: Props) {
  const filterState = { root_agent_id: filters.root_agent_id ?? '' }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  function openDrill(rootId: number) {
    router.get(PATH, {
      ...(filterState.root_agent_id ? { root_agent_id: filterState.root_agent_id } : {}),
      preset: date_range.preset,
      ...(date_range.preset === 'custom' ? { start_date: date_range.start_date, end_date: date_range.end_date } : {}),
      drill_root_id: rootId
    }, { preserveState: false })
  }

  function closeDrill() {
    router.get(PATH, {
      ...(filterState.root_agent_id ? { root_agent_id: filterState.root_agent_id } : {}),
      preset: date_range.preset,
      ...(date_range.preset === 'custom' ? { start_date: date_range.start_date, end_date: date_range.end_date } : {})
    }, { preserveState: false })
  }

  const columns: Column<Row>[] = [
    { key: 'root_name', header: 'Root Agent', sortable: true,
      render: (r) => (
        <button
          type="button"
          onClick={() => openDrill(r.id)}
          className="text-chestnut-forest hover:underline font-medium text-left"
        >
          {r.root_name}
        </button>
      ) },
    { key: 'downline_size', header: 'Downline', sortable: true, className: 'text-center' },
    { key: 'producing_agents', header: 'Producers', sortable: true, className: 'text-center' },
    { key: 'gross_amount', header: 'Gross $', sortable: true, render: (r) => money(r.gross_amount) },
    { key: 'agent_amount', header: 'Agent $', sortable: true, render: (r) => money(r.agent_amount) },
    { key: 'override_earned', header: 'Override Earned', sortable: true, render: (r) => money(r.override_earned) },
    { key: 'equity_credits', header: 'Equity Credits', sortable: true, render: (r) => r.equity_credits.toLocaleString() }
  ]

  const drillColumns: Column<DrillRow>[] = [
    { key: 'agent_name', header: 'Downline Agent', sortable: true,
      render: (r) => (
        <Link href={`/agents/${r.id}`} className="text-chestnut-forest hover:underline">{r.agent_name}</Link>
      ) },
    { key: 'policies', header: 'Policies', sortable: true, className: 'text-center' },
    { key: 'gross_amount', header: 'Gross $', sortable: true, render: (r) => money(r.gross_amount) },
    { key: 'agent_amount', header: 'Agent $', sortable: true, render: (r) => money(r.agent_amount) },
    { key: 'equity_credits', header: 'Equity Credits', sortable: true, render: (r) => r.equity_credits.toLocaleString() }
  ]

  return (
    <AppLayout>
      <Head title="Downline Production" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Downline Production</h1>
          <p className="text-chestnut-charcoal/70 mt-1">
            Production rolled up across each agent's entire downline. Defaults to top-level sponsors;
            pick a specific agent to recenter the report on their subtree.
          </p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Root agent"
            value={filters.root_agent_id ?? ''}
            options={all_agents.map(a => ({ value: a.id, label: a.name }))}
            onChange={(v) => applyFilter(PATH, { ...filterState, preset: date_range.preset, start_date: date_range.start_date, end_date: date_range.end_date }, 'root_agent_id', v)}
            allLabel="All top-level sponsors"
          />
        </ReportControlBar>

        <ChartCard
          title="Top 10 downlines — agent vs override $"
          subtitle="Stacked bars show what each root agent's downline pays out as agent commissions vs. overrides earned upstream."
          empty={rows.length === 0}
          emptyMessage="No downline production in this date range."
        >
          <StackedBarChart
            data={[...rows]
              .sort((a, b) => (b.gross_amount) - (a.gross_amount))
              .slice(0, 10)
              .map(r => ({
                label: r.root_name,
                agent_amount: r.agent_amount,
                override_earned: r.override_earned
              }))}
            keys={[
              { key: 'agent_amount', label: 'Agent $', color: ChartColors.FOREST },
              { key: 'override_earned', label: 'Override $', color: ChartColors.AMBER }
            ]}
            valueFormatter={money}
          />
        </ChartCard>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search agents..."
            searchKeys={['root_name']}
            pageSize={20}
            emptyMessage="No downline production in this date range."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'gross_amount', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>

        {drill_root_id && (
          <div className="bg-white rounded-xl border border-chestnut-gray p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-chestnut-charcoal">
                Downline breakdown — {drill_root_name}
              </h2>
              <button
                type="button"
                onClick={closeDrill}
                className="text-sm text-chestnut-charcoal/70 hover:text-chestnut-charcoal underline"
              >
                Close drill-in
              </button>
            </div>
            <DataTable
              data={drill_rows}
              columns={drillColumns}
              searchPlaceholder="Search downline..."
              searchKeys={['agent_name']}
              pageSize={25}
              emptyMessage="No agents in this downline."
              getRowKey={(r) => r.id}
              defaultSortKey="gross_amount"
              defaultSortDirection="desc"
            />
          </div>
        )}
      </div>
    </AppLayout>
  )
}
